/**
 * react-router pattern for the full-page portfolio view. Also the key under
 * which it is registered in ROUTE_PERMISSION_REQUIREMENTS. Like every route,
 * only reachable in-app: the PIN gate is React state.
 */
export const PORTFOLIO_PAGE_ROUTE = "/students/:studentId/portfolio"

export const getPortfolioPagePath = (studentId: string): string =>
  `/students/${encodeURIComponent(studentId)}/portfolio`

export interface PortfolioBreadcrumbRoot {
  label: string
  path: string
}

const BREADCRUMB_ROOTS: readonly PortfolioBreadcrumbRoot[] = [
  { label: "Teacher view", path: "/teacher" },
  { label: "Front desk", path: "/front-desk" },
  { label: "Students", path: "/students" },
]

const DEFAULT_ROOT = BREADCRUMB_ROOTS[BREADCRUMB_ROOTS.length - 1]

const getBreadcrumbRoot = (
  fromPathname: string | undefined
): PortfolioBreadcrumbRoot => {
  if (!fromPathname) return DEFAULT_ROOT
  return (
    BREADCRUMB_ROOTS.find(
      (root) =>
        fromPathname === root.path || fromPathname.startsWith(`${root.path}/`)
    ) ?? DEFAULT_ROOT
  )
}

/** Shape of the `location.state` the entry buttons push. */
interface PortfolioLocationState {
  from?: string
}

const readLocationState = (state: unknown): PortfolioLocationState => {
  if (!state || typeof state !== "object") return {}
  const from = (state as { from?: unknown }).from
  return typeof from === "string" ? { from } : {}
}

export interface PortfolioBreadcrumb {
  root: PortfolioBreadcrumbRoot
  /** Where "back" (Esc, the breadcrumb) goes. */
  backPath: string
}

/**
 * The portfolio is reached from the Students table, the teacher view, and the
 * front desk. History state carries the origin pathname so Back means "back
 * to where I came from".
 */
export const getPortfolioBreadcrumb = (
  locationState: unknown
): PortfolioBreadcrumb => {
  const { from } = readLocationState(locationState)
  const root = getBreadcrumbRoot(from)
  return { root, backPath: root.path }
}
